import type { PnidWizardState } from '../types';
import { normalizeAssetTag } from './normalizeAssetTag';

/** One selected canvas annotation, as the workspace addresses it to the backend. */
export interface SelectedAnnotation {
  kind: 'equipment' | 'instrument' | 'node' | 'controlLoop' | 'relationship';
  id: string;
}

/** What a canvas selection offers a redundancy set. */
export interface RedundancySetSelection {
  /** The tags of the selected tagged equipment and tagged instruments, as stored. */
  tags: string[];
  /** A name for every selected item that carries no tag, and so cannot be a member. */
  untaggedItems: string[];
  /** How many different tags `tags` holds once separators are normalized. */
  distinctTagCount: number;
}

/**
 * Only a tag is a plant-wide identity, so a line, a control loop, an untagged
 * instrument or an equipment with no tag cannot join a redundancy set.
 */
export function resolveRedundancySetSelection(
  selection: SelectedAnnotation[],
  state: Pick<PnidWizardState, 'equipments' | 'instruments' | 'nodes'>,
): RedundancySetSelection {
  const tags: string[] = [];
  const untaggedItems: string[] = [];

  for (const item of selection) {
    if (item.kind === 'equipment') {
      const equipment = state.equipments.find((candidate) => candidate.id === item.id);
      const tag = equipment?.equipment_no?.trim();
      if (tag) tags.push(tag);
      else untaggedItems.push(equipment?.equipment_type || 'an equipment with no tag');
    } else if (item.kind === 'instrument') {
      const instrument = state.instruments.find((candidate) => candidate.id === item.id);
      const tag = instrument?.tag_status === 'tagged' ? instrument.instrument_text?.trim() : '';
      if (tag) tags.push(tag);
      else untaggedItems.push(instrument?.generated_id || instrument?.instrument_type || 'an untagged instrument');
    } else if (item.kind === 'node') {
      const node = state.nodes.find((candidate) => candidate.id === item.id);
      untaggedItems.push(node?.line_no ? `line ${node.line_no}` : 'a line');
    } else {
      untaggedItems.push(item.kind === 'controlLoop' ? 'a control loop' : 'a relationship');
    }
  }

  return {
    tags,
    untaggedItems,
    distinctTagCount: new Set(tags.map(normalizeAssetTag)).size,
  };
}
